/**
 * Renders the PDF pages. Note that this is just a div that contains
 * a set of multiple <Page>'s
 */
import React from "react";
import ReactDOM from "react-dom";
import $ from "jquery";

import Page from "./page";
import Controls from "./controls";

import * as Utils from "../documents/utils";
import * as RenderingUtils from "../documents/rendering_utils";
import * as URLUtils from "../documents/url_utils";

export default class extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      maxWidth: 0,
      maxHeight: 0,
      recalcPagePositions: false,

      documentWidth: 0,
      documentHeight: 0,

      scrollPositionX: 0,
      scrollPositionY: 0,
      recalcVisiblePages: false,
      visiblePageIndexes: [],

      correctedScrollOffsetX: 0,
      correctedScrollOffsetY: 0,
      correctScrollOffset: false,

      scrollToPage: true,
      scrollToPageNumber: props.initialPageNumber,

      scrollDrag: false,
      scrollStartX: 0,
      scrollStartY: 0,
      scrollMouseStartX: 0,
      scrollMouseStartY: 0,

      pagePositions: [],
      mostVisiblePageIndex: 0,
      zoom: 100,

      initialZoomSet: false
    }
  }

  /**
   * Is triggered when component is rendered on page for first time
   */
  componentDidMount() {
    // rebind local methods
    this.handleResize = this.handleResize.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    this.handleScrollTimerComplete = this.handleScrollTimerComplete.bind(this)
    this.handleResizeTimerComplete = this.handleResizeTimerComplete.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleMouseUp = this.handleMouseUp.bind(this);

    // add resize handler
    $(window).bind("resize", this.handleResize);

    // add scroll listener
    var scrollArea = ReactDOM.findDOMNode(this.refs.scrollArea);
    $(scrollArea).bind("scroll", this.handleScroll);

    this.recalcCanvasSize();
  }

  /**
   * Is fired when component is re-rendered
   */
  componentDidUpdate() {
    // should we re-calculate page positions?
    if(this.state.recalcPagePositions) {
      this.recalcPagePositions();
      return;
    }

    // recalculate which pages are visible?
    if(this.state.recalcVisiblePages) {
      this.recalcVisiblePages();
      return;
    }

    // set initial zoom level in needed
    if(!this.state.initialZoomSet && this.state.maxWidth > 0 && this.state.maxHeight > 0) {
      this.setInitialZoom();
      return;
    }

    // should we scroll to a correct scroll offset?
    if(this.state.correctScrollOffset) {
      this.correctScrollOffset();
    }

    // should we scroll to a given page?
    if(this.state.scrollToPage) {
      this.setState({ scrollToPage: false });
      this.scrollToPage(this.state.scrollToPageNumber);
    }
  }

  /**
   * Is triggered when component is removed from page
   */
  componentWillUnmount() {
    var scrollArea = ReactDOM.findDOMNode(this.refs.scrollArea);

    this.clearScrollTimer();
    this.clearResizeTimer();

    // unbind event handler to prevent mem leaks
    $(window).unbind("resize", this.handleResize);
    $(window).unbind("mousemove", this.handleMouseMove);
    $(window).unbind("mouseup", this.handleMouseUp);
    $(scrollArea).unbind("scroll", this.handleScroll);
  }

  /**
   * Is triggered when component will receive new properties
   */
  componentWillReceiveProps(nextProps) {
    // did the page number change?
    if(this.props.initialPageNumber != nextProps.initialPageNumber) {
      // check if page is correct
      if(nextProps.initialPageNumber > this.props.pages.length) return;

      this.setState({
        scrollToPage: true,
        scrollToPageNumber: nextProps.initialPageNumber
      });
    }
  }

  /**
   * Corrects scroll offset
   */
  correctScrollOffset() {
    var scrollArea = $(ReactDOM.findDOMNode(this.refs.scrollArea));

    scrollArea.scrollLeft(this.state.correctedScrollOffsetX);
    scrollArea.scrollTop(this.state.correctedScrollOffsetY);

    this.setState({ correctScrollOffset: false });
  }

  /**
   * Returns the current DOM max canvas width
   */
  getCanvasWidth() {
    var canvas = $(ReactDOM.findDOMNode(this.refs.canvas));
    return canvas.width();
  }

  /**
   * Returns the current DOM max canvas width
   */
  getCanvasHeight() {
    var canvas = $(ReactDOM.findDOMNode(this.refs.canvas));
    return canvas.height();
  }

  /**
   * Checks if canvas changed size
   */
  didCanvasSizeChange() {
    // did, after render, the canvas change?
    return this.state.maxWidth != this.getCanvasWidth() ||
           this.state.maxHeight != this.getCanvasHeight();
  }

  /**
   * Do initial zoom
   */
  setInitialZoom() {
    let page;

    if(this.props.initialPageNumber) {
      for(let findPage of this.props.pages) {
        if(findPage.pageNumber == this.props.initialPageNumber) {
          page = findPage;
          break;
        }
      }
    }

    if(!page) page = this.props.pages[0];

    var zoom = RenderingUtils.getIdealZoom(
      false,
      page.width,
      page.height,
      this.state.maxWidth,
      this.state.maxHeight
    );

    this.setZoom(zoom, true, page.pageNumber);
    this.setState({ initialZoomSet: true });
  }

  /**
   * Recalcs canvas size
   */
  recalcCanvasSize() {
    var scrollArea = $(ReactDOM.findDOMNode(this.refs.scrollArea));

    this.setState({
      maxWidth: this.getCanvasWidth(),
      maxHeight: this.getCanvasHeight(),
      recalcPagePositions: true,
      recalcVisiblePages: true
    });
  }

  /**
   * Recalcs position of page canvas
   */
  recalcPagePositions() {
    // calculate where the pages should go in the canvas
    var pagePositions = RenderingUtils.getPagePositions(
      this.props.pages,
      this.state.zoom
    );

    // change state
    this.setState({
      pagePositions: pagePositions.positions,
      documentWidth: pagePositions.totalWidth,
      documentHeight: pagePositions.totalHeight,
      recalcPagePositions: false,
      recalcVisiblePages: true
    });
  }

  /**
   * Recalcs the visible pages and fires a onChange event when applicable.
   * Important: This should be called *after* an update.
   */
  recalcVisiblePages() {
    // calculate which pages should be visible to the user
    var visiblePageIndexes = RenderingUtils.getVisiblePageIndexes(
      this.state.pagePositions,
      this.state.scrollPositionY,
      this.state.maxHeight
    );

    // calculate which page is MOST visible to the user
    var mostVisiblePageIndex = RenderingUtils.getMostVisiblePageIndex(
      this.state.pagePositions,
      this.state.scrollPositionY,
      this.state.maxHeight,
      visiblePageIndexes
    );

    // fire event that most visible page has changed
    if(mostVisiblePageIndex != null && this.state.mostVisiblePageIndex != mostVisiblePageIndex && this.props.onMostVisiblePageChange) {
      this.props.onMostVisiblePageChange(this.props.pages[mostVisiblePageIndex]);
    }

    this.setState({
      mostVisiblePageIndex: mostVisiblePageIndex,
      visiblePageIndexes: visiblePageIndexes,
      recalcVisiblePages: false
    });
  }

  /**
   * Jumps to given page number
   */
  scrollToPage(pageNumber) {
    var page = this.state.pagePositions[pageNumber - 1];
    if(!page) return;

    var scrollArea = $(ReactDOM.findDOMNode(this.refs.scrollArea));

    var y = page.y1 - 0.5 * RenderingUtils.PAGE_MARGIN * this.state.zoom / 100,
        x = this.getFillerWidth() / 2 - this.state.maxWidth / 2;

    if(y < 0) y = 0;
    if(x < 0) x = 0;

    scrollArea.scrollTop(y);
    scrollArea.scrollLeft(x);
  }

  /**
   * Is fired when page is resized
   */
  handleResize(event) {
    this.clearResizeTimer();

    this.resizeTimer = window.setTimeout(
      this.handleResizeTimerComplete,
      500
    );
  }

  /**
   * Resizes canvas
   */
  handleResizeTimerComplete() {
    // tests if DOM element changed size
    // if so, trigger a state change of max-width, height
    // this will also cause everything to be re-calculated
    if(this.didCanvasSizeChange()) {
      this.recalcCanvasSize();
      return;
    }
  }

  /**
   * Kills document scroll interval
   */
  clearScrollTimer() {
    if(this.scrollTimer) {
      window.clearTimeout(this.scrollTimer);
      this.scrollTimer = null;
    }
  }

  /**
   * Kills resize timer interval
   */
  clearResizeTimer() {
    if(this.resizeTimer) {
      window.clearTimeout(this.resizeTimer);
      this.resizeTimer = null;
    }
  }

  /**
   * Is fired when page is scrolled
   */
  handleScroll(event) {
    this.clearScrollTimer();

    this.scrollTimer = window.setTimeout(
      this.handleScrollTimerComplete,
      100
    );
  }

  /**
   * Is fired when scroll is ready to roll
   */
  handleScrollTimerComplete() {
    var scrollArea = $(ReactDOM.findDOMNode(this.refs.scrollArea));

    this.setState({
      recalcVisiblePages: this.scrollPositionY != scrollArea.scrollTop(),
      scrollPositionX: scrollArea.scrollLeft(),
      scrollPositionY: scrollArea.scrollTop()
    });
  }

  /**
   * Is fired when fit-page setting is changed
   */
  handleFitPageChange(fitPageWidth) {
    var visiblePage = this.props.pages[this.state.mostVisiblePageIndex];

    var zoom = RenderingUtils.getIdealZoom(
      fitPageWidth,
      visiblePage.width,
      visiblePage.height,
      this.state.maxWidth,
      this.state.maxHeight
    );

    this.setZoom(zoom, true);
  }

  /**
   * Fires when user presses zoom-in
   */
  handleZoomOut() {
    var zoom = this.state.zoom / ((100 + RenderingUtils.ZOOM_STEP) / 100);
    zoom = zoom < RenderingUtils.ZOOM_MIN ? this.state.zoom : zoom;

    this.setZoom(zoom, false);
  }

  /**
   * Fires when user presses zoom-in
   */
  handleZoomIn() {
    var zoom = this.state.zoom * (100 + RenderingUtils.ZOOM_STEP) / 100;
    zoom = zoom > RenderingUtils.ZOOM_MAX ? this.state.zoom : zoom;

    this.setZoom(zoom, false);
  }

  /**
   * Is fired when mouse button is pressed
   */
  handleScrollAreaMouseDown(event) {
    // mobile is not interesting
    if(Utils.isOnMobile()) return;
    if(!this.isEventApplicableForScroll(event)) return;

    // initiate scroll drag
    if(!this.state.scrollDrag) {
      // add mousemove event listener
      $(window).bind("mousemove", this.handleMouseMove);
      $(window).bind("mouseup", this.handleMouseUp);

      this.setState({
        scrollDrag: true,
        scrollStartX: this.state.scrollPositionX,
        scrollStartY: this.state.scrollPositionY,
        scrollMouseStartX: event.pageX,
        scrollMouseStartY: event.pageY
      });
    }
  }

  /**
   * Is fired when mouse moves
   */
  handleMouseMove(event) {
    // not interested in event when there is no drag going on
    if(!this.state.scrollDrag) return;

    var scrollArea = $(ReactDOM.findDOMNode(this.refs.scrollArea));

    // test if we want to scroll if cursor is out of reach
    var movedX = this.state.scrollMouseStartX - event.pageX,
        movedY = this.state.scrollMouseStartY - event.pageY;

    var newScrollX = this.state.scrollStartX + movedX,
        newScrollY = this.state.scrollStartY + movedY;

    if(this.hasHorizontalScroll()) scrollArea.scrollLeft(newScrollX);
    scrollArea.scrollTop(newScrollY);
  }

  /**
   * Tests if given event is applicable for controlling scroll
   */
  isEventApplicableForScroll(event) {
    // grab the target of the event
    var target = $(event.target);

    // ok this is tricky, but we want to ignore everything controlslayer related
    // since we want the user to be able to click on the controls *in* the page
    // the rest is considered a _drag_
    return target.parent(".document-viewer-page-controlslayer").length == 0;
  }

  /**
   * Is fired when mouse button is released
   */
  handleMouseUp(event) {
    if(Utils.isOnMobile()) return;
    if(!this.isEventApplicableForScroll(event)) return;

    // remove mouse move event listener
    $(window).unbind("mousemove", this.handleMouseMove);
    $(window).unbind("mouseup", this.handleMouseUp);

    if(this.state.scrollDrag) {
      this.setState({ scrollDrag: false });
    }
  }

  /**
   * Zooms document to given level
   */
  setZoom(zoom, scrollToPage, scrollToPageNumberOverride = null) {
    var scrollArea = $(ReactDOM.findDOMNode(this.refs.scrollArea));

    var positionX = scrollArea.scrollLeft(),
        positionY = scrollArea.scrollTop();

    var halfCanvasHeight = 0.5 * this.state.maxHeight,
        halfCanvasWidth = 0.5 * this.state.maxWidth;

    var zoomFactor = zoom / this.state.zoom;

    var state = {
          recalcPagePositions: true,
          zoom: zoom
        };

    if(scrollToPage) {
      state.scrollToPage = true;
      state.scrollToPageNumber = scrollToPageNumberOverride ? scrollToPageNumberOverride : this.props.pages[this.state.mostVisiblePageIndex].pageNumber;
    } else {
      state.correctScrollOffset = true;
      state.correctedScrollOffsetX = (positionX + halfCanvasWidth) * zoomFactor - halfCanvasWidth;
      state.correctedScrollOffsetY = (positionY + halfCanvasHeight) * zoomFactor - halfCanvasHeight;
    }

    this.setState(state);
  }

  /**
   * Gets the width of the filler <div>
   * This is a bit wider than the total documentWidth (since we want controls
   * to be visible at all times)
   */
  getFillerWidth() {
    var canvasWidth = this.state.documentWidth + RenderingUtils.PAGE_MARGIN * 2;

    if(canvasWidth < this.state.maxWidth)
      canvasWidth = this.state.maxWidth;

    return canvasWidth;
  }

  /**
   * Tests if canvas should have horizontal scroll
   */
  hasHorizontalScroll() {
    return parseInt(this.getFillerWidth()) > this.state.maxWidth;
  }

  /**
   * Renders the (for the user) visible pages
   */
  renderVisiblePages() {
    var visiblePages = [];

    // walk over all page indexes that should be visible to user
    for(let index of this.state.visiblePageIndexes) {
      let page = this.props.pages[index];
      let pagePosition = this.state.pagePositions[index];
      let debug;

      // when in debug, render page number on top of page
      if(this.props.debug) {
        debug = `${this.props.pages[index].pageNumber}`;
      }

      // render the individual page
      // React will automatically purge page when it's not visible anymore
      // because we don't render it anymore here
      visiblePages.push(
        <Page
          debug={debug}
          allowShare={this.props.allowShare}
          downloadUrl={page.downloadPath}
          pdfUrl={page.pdfPath}
          x={this.getFillerWidth() / 2 - pagePosition.width / 2}
          y={pagePosition.y1}
          scale={pagePosition.width / page.width}
          width={pagePosition.width}
          height={pagePosition.height}
          highlightedTerms={this.props.highlightedTerms}
          page={page.pageNumber}
          sharePath={URLUtils.getShareUrl(page.sharePath)}
          isFavorite={this.props.favorites.indexOf(page.id) != -1}
          onFavoriteClick={this.handleFavoriteClick.bind(this, page)}
          key={`page-${page.pageNumber}`} />
      );
    }

    return visiblePages;
  }

  /**
   * Is fired when favorite is clicked on page
   */
  handleFavoriteClick(page, event) {
    this.props.onFavoriteClick(page, event);
  }

  render() {
    var visiblePages = [],
        className = "document-viewer-canvas-scroll",
        fillerStyle = {
          width: parseInt(this.getFillerWidth()),
          height: parseInt(this.state.documentHeight)
        };

    // do we actually need a horizontal scrollbar?
    if(this.hasHorizontalScroll()) {
      className += " document-viewer-canvas-scroll--horizontal";
    }

    // are we dragging?
    if(this.state.scrollDrag) {
      className += " document-viewer-canvas-scroll--dragging";
    }

    return (
      <div className="document-viewer-canvas" ref="canvas">
        <div
          className={className}
          ref="scrollArea"
          onMouseDown={this.handleScrollAreaMouseDown.bind(this)}>
          <div className="document-viewer-canvas-filler" style={fillerStyle} />
          {this.renderVisiblePages()}
        </div>
        <Controls
          allowDownload={this.props.allowDownload}
          downloadUrl={this.props.downloadUrl}
          onFitPageChange={this.handleFitPageChange.bind(this)}
          onZoomIn={this.handleZoomIn.bind(this)}
          onZoomOut={this.handleZoomOut.bind(this)} />
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./app/javascript/portal/document_viewer/pages.jsx